import React, { useEffect, useRef, useContext, ContextType, useCallback, useMemo } from "react";
import { marked } from "marked";
import { Badge, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import InterfaceContext, { SupportedLanguage, OnlineAwarenessContext, OfflineStatus, OfflineSyncContext } from "./Context";
import * as v0 from "@aidkitorg/types/lib/survey";
import { PublicConfig } from "@aidkitorg/types/lib/config";
import { createLogger, DefaultColors, DefaultStyles, offlineLogger } from "./utils";
import { SyncState, SyncStateEvent, SyncStatus, toApplicant } from "./offline/routes";
import { captureException } from "@sentry/react";
import { getOrCreateHttpCache, PermaCache, RequestEntry } from "./offline";
import { ChangeSet } from "@aidkitorg/typesheets/lib/collab";
import { useAsyncEffect } from "./Hooks/AsyncEffect";
import { tryRemovePortalToken } from "./Hooks/PortalSession";

import { useSurveySections } from "./Contexts/SurveySectionsContext";
import type { Question, Sections } from "./Apply";
import { Button, Icon, nonHookMarkdown } from "@aidkitorg/component-library";
import { useLocalizedStrings } from "./Localization";
import { QuestionProps } from "./Questions/Props";


export const BUTTON_CLASS = "cursor-pointer shadow mt-2 inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"

export const mapStrToList = (str: string) => {
  return str.replace(/\n/g, " ").replace(/,/g, " ").split(" ").filter((l: string) => l.length > 0).map(t => t.trim());
}

export default function SlideOut(props: { title: string, content: HTMLDivElement | null, open: boolean, setOpen: (open: boolean) => void }) {
  return (
    <Transition.Root show={props.open} as={React.Fragment}>
      <Dialog as="div" className="z-50 fixed inset-0 overflow-hidden" onClose={props.setOpen}>
        <div className="absolute inset-0 overflow-hidden">
          <Dialog.Overlay className="absolute inset-0" />

          <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
            <Transition.Child
              as={React.Fragment}
              enter="transform transition ease-in-out duration-500 sm:duration-700"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-500 sm:duration-700"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <div className="w-screen max-w-md">
                <div className="h-full flex flex-col py-6 bg-white shadow-xl overflow-y-scroll">
                  <div className="px-4 sm:px-6">
                    <div className="flex items-start justify-between">
                      <Dialog.Title className="text-lg font-medium text-gray-900">{props.title}</Dialog.Title>
                      <div className="ml-3 h-7 flex items-center">
                        <button
                          type="button"
                          className="bg-white border-0 rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                          onClick={() => props.setOpen(false)}
                        >
                          <span className="sr-only">Close panel</span>
                          <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 relative flex-1 px-4 sm:px-6">
                    {/* Replace with your content */}
                    <div ref={(el) => props.content && el?.appendChild(props.content)} className="absolute inset-0 px-4 sm:px-6" />
                    {/* /End replace */}
                  </div>
                </div>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}

function NodeWithSlideout(props: { content: string }) {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState<HTMLDivElement | null>(null)
  const [open, setOpen] = useState(false);

  function toggle(e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    console.log((e.target as HTMLElement).previousSibling?.previousSibling?.nodeName);
    if ((e.target as HTMLElement).previousSibling?.previousSibling?.nodeName === "HR") {
      let toShow = (e.target as HTMLElement).nextSibling;
      const newContent = document.createElement('div');
      while (toShow && toShow.nodeName !== "HR") {
        newContent.appendChild(toShow.cloneNode(true));
        toShow = toShow.nextSibling;
      }
      setContent(newContent);
      setTitle((e.target as HTMLElement).innerText);
      setOpen(true);
    }
  }

  return <>
    <div onClick={toggle} className="markdown" dangerouslySetInnerHTML={{ __html: props.content }}></div>
    <SlideOut title={title} content={content} open={open} setOpen={setOpen} />
  </>;

}

export function useMarkdown(str?: string,
  collapsible?: boolean,
  noParagraphs?: boolean,
  replaceWithHtml?: {
    regex: RegExp, html: string
  },
  tableRenderer?: (tableHtml: string) => React.ReactNode
) {
  const context = useContext(InterfaceContext);

  return nonHookMarkdown(str, collapsible, noParagraphs, replaceWithHtml, context.textAlign, tableRenderer);
}

interface UseIntervalOptions {
  /**
   * If true, this effect will run even if the user is not viewing this page currently
   */
  runInBackground: boolean;
  fireImmediately: boolean;
}

export function useInterval(callback: () => void, time: number, options: Partial<UseIntervalOptions> = {}) {
  const savedCallback = useRef<() => void>();

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  useEffect(() => {
    function tick() {
      if (document.hidden && !options.runInBackground) return;
      const memoizedCallback = savedCallback.current;
      memoizedCallback?.();
    }

    if (options.fireImmediately) {
      tick();
    }

    const id = setInterval(tick, time);
    return () => clearInterval(id);
  }, [time]);
}

export function useAsyncInterval<T extends () => Promise<void>>(callback: T, millis: number) {

  const tick = useCallback(async () => {
    if (document.hidden) return;
    await callback();
  }, [callback]);

  // Set up the interval.
  useAsyncEffect(async (cancel) => {
    if (millis !== null) {
      let id = setInterval(tick, millis);
      cancel.onabort = () => clearInterval(id);
    }
  }, [tick, millis]);
}

function copyToClipboardFallback(str: string) {
  const el = document.createElement("textarea");
  el.value = str;
  el.setAttribute("readonly", "");
  el.style.position = "absolute";
  el.style.left = "-9999px";
  document.body.appendChild(el);
  el.select();
  document.execCommand("copy");
  document.body.removeChild(el);
}

export function copyToClipboard(str: string) {
  // navigator.clipboard is undefined unless inSecureContext (https)
  // so this won't work on dev safari, but will work in prod safari. (crosses fingers)
  if (navigator.clipboard !== undefined) {
    navigator.clipboard.writeText(str).then(() => {
    }, () => {
      copyToClipboardFallback(str);
    });
  } else {
    copyToClipboardFallback(str);
  }
}

export function snakeToEnglish(str: string) {
  return (str || '')
    .replace(/_([-0-9a-z])/g, (c) => " " + c.slice(1).toUpperCase())
    .replace(/^[-a-z]/g, (c) => c.toUpperCase());
}

// Sanitizes phone number input by removing invalid characters and enforcing max length
// param input - The raw input string
// returns Sanitized phone number string (digits and leading + symbol only, max 14 chars)

export function sanitizePhoneNumberInput(input: string): string {
  let sanitized = input || '';

  // Remove any + signs that aren't directly preceded by a digit
  sanitized = sanitized.replace(/(\d)\+/g, '$1');

  // Remove any characters that are not digits or + symbols
  sanitized = sanitized.replace(/[^\d+]/g, '');

  // Enforce max length of 14 characters
  if (sanitized.length > 14) {
    sanitized = sanitized.slice(0, 14);
  }

  return sanitized;
}

export type SupportedLanguageContent = "English Content" | "Spanish Content" | SupportedLanguage;
type LegacySupportedLanguage = "english" | "spanish" | "";

export function languageContent(lang?: SupportedLanguage | LegacySupportedLanguage): SupportedLanguageContent {
  if (lang === 'es' || lang === 'spanish') {
    return 'Spanish Content';
  } else if (lang === 'en' || lang === 'english' || !lang) {
    return 'English Content';
  }
  return lang;
}

export function safeParse(json: string, safeReturn?: any) {
  try {
    return JSON.parse(json);
  } catch (e) {
    if (typeof safeReturn !== 'undefined') return safeReturn;
    console.warn("Error with safe parse", e, json);
    return {};
  }
}

export function safeParseUrl(maybeUrlString: string, orElse?: URL) {
  try {
    return new URL(maybeUrlString);
  } catch (e) {
    console.warn('safeParseUrl failed with error:', e);
    return orElse;
  }
}

// Get formula from legacy (metadata is plain string) or v0 (metadata is object) Validated types.
export function safeParseValidatedFormula(metadata: string) {
  try {
    if (JSON.parse(metadata).formula) {
      return JSON.parse(metadata).formula;
    } else {
      return ''
    }
  } catch (e) {
    return metadata;
  }
}

export function delimitNumbers(str: string) {
  return (str + "").replace(/\b(\d+)((\.\d+)*)\b/g, function(a, b, c) {
    return (
      (b.charAt(0) > 0 && !(c || ".").lastIndexOf(".")
        ? b.replace(/(\d)(?=(\d{3})+$)/g, "$1,")
        : b) + c
    );
  });
}

// Utility function to get a badge based on status
export function getBadgeVariant(status: string, passedString: string, failedString: string) {

  const lcStatus = status.toLowerCase();

  if (lcStatus === "verified" || lcStatus === "passed") {
    return <Badge variant="success">{passedString}</Badge>
  }

  return <Badge variant="warning">{failedString}</Badge>;

}

export function getApplicantLink(deployment: string, thisDeployment: string, uid: string, label: string) {

  if (deployment !== thisDeployment) {
    return <a href={`https://${deployment}.aidkit.org/applicant/${uid}`} target="_blank" rel="noreferrer noopener nofollow">{label}</a>
  }

  return <Link to={"/applicant/" + uid} target="_blank" rel="noreferrer noopener nofollow">{label}</Link>
}

export function RigidSpinner(props?: any) {
  return <Spinner {...props} as="span" animation="border" size="sm" role="status" aria-hidden="true" />;
}

export function SpacedSpinner(props?: any) {
  return <><RigidSpinner {...props} />&nbsp;</>;
}

export function AidKitLogo(props: { width: number, height?: number }) {
  return (
    <svg version="1.0" xmlns="http://www.w3.org/2000/svg" width={props.width} viewBox="0 0 5252 1032" preserveAspectRatio="xMidYMid meet">
      <g fill="#000000">
        <path d="M1569.4 1030.9 c-62.4 -3.6 -118.1 -23.5 -165.9 -59.3 -13.4 -10 -39.2 -35.2 -49.9 -48.6 -26.3 -32.9 -50.2 -80.7 -63.2 -125.8 -12 -41.7 -17.4 -84.1 -17.4 -136.2 0 -29.7 0.9 -45.7 4.1 -70 9.5 -74 37.3 -142.2 79.9 -196.1 10.6 -13.4 37.5 -40.2 51 -50.9 46.6 -37 101.4 -58.9 162 -65 15.7 -1.6 52.2 -1.3 69.5 0.5 50.5 5.3 97.7 22.7 130 48 21.5 16.8 42.9 42.8 57.5 69.8 4.9 9.1 5.4 9.7 8.3 9.7 l3.2 0 -1.2 -21.2 c-3.2 -59 -3.6 -78.6 -4 -229.6 l-0.5 -156.2 64.6 0 64.6 0 0 507 0 507 -61.5 0 -61.5 0 0 -56 0 -56 -3.4 0 c-3.2 0 -3.6 0.4 -7.6 7.8 -22.7 42.1 -57.3 75 -97.4 92.5 -39.5 17.3 -76.8 26.1 -120.6 28.7 -19.2 1.1 -20.2 1.1 -40.6 -0.1z m72.4 -112 c61.4 -6.6 114.7 -40.6 147.9 -94.2 11.9 -19.4 23.6 -46.1 30.3 -69.6 16.8 -59.2 16.8 -139.4 0 -200 -16.6 -59.2 -47 -104.1 -90.4 -133.2 -54.2 -36.4 -124.8 -43.1 -185.7 -17.6 -22 9.2 -40 21.2 -58.2 38.7 -20.5 19.6 -31.8 35.4 -45.2 62.5 -14.1 28.5 -22.8 56.6 -27.9 89.8 -4 25.5 -4.9 42.7 -4.3 75.1 0.7 35.2 3.1 56.1 9.3 81.1 10 40.4 28.6 77.3 52.9 104.7 36 40.8 87.9 63.2 147.3 63.7 7.8 0.1 18.6 -0.4 24 -1z"/>
        <path d="M1.5 1012.3 c0.3 -1 85 -228.4 188 -505.3 103.1 -276.9 187.7 -504.3 188 -505.3 0.7 -1.6 5.4 -1.7 79.9 -1.7 l79.1 0 187.5 503.8 c103 277 188 505.2 188.7 507 l1.3 3.2 -70.6 -0.2 -70.6 -0.3 -47.6 -132.2 -47.7 -132.3 -223 0 -223 0 -34.4 95.3 c-18.9 52.3 -40.5 112 -47.9 132.5 l-13.5 37.2 -67.4 0 c-63.9 0 -67.5 -0.1 -66.8 -1.7z m632.5 -382 c0 -0.5 -39.7 -110.7 -88.2 -245 l-88.3 -244.3 -3.1 0 -3.1 0 -88.1 244 c-48.5 134.1 -88.2 244.4 -88.2 245 0 0.7 55.6 1 179.5 1 98.7 0 179.5 -0.3 179.5 -0.7z"/>
        <path d="M1032 654.5 l0 -359.5 65 0 65 0 0 359.5 0 359.5 -65 0 -65 0 0 -359.5z"/>
        <path d="M2107 507 l0 -507 67.5 0 67.5 0 0 272.7 0 272.7 3.4 -3.3 c1.9 -1.9 116.1 -124.6 253.8 -272.7 l250.3 -269.3 78.8 -0.1 c43.3 0 78.7 0.2 78.7 0.5 0 0.4 -391 414.1 -411.8 435.7 l-4.5 4.6 217.2 286.4 217.1 286.3 -75.2 0.3 c-41.4 0.1 -75.8 0 -76.3 -0.2 -0.6 -0.2 -84.5 -108 -186.5 -239.5 -102 -131.5 -185.6 -239.1 -185.9 -239 -0.3 0 -36.3 38.1 -79.8 84.6 l-79.3 84.5 0 154.9 0 154.9 -67.5 0 -67.5 0 0 -507z"/>
        <path d="M3021 654.5 l0 -359.5 64.5 0 64.5 0 0 359.5 0 359.5 -64.5 0 -64.5 0 0 -359.5z"/>
        <path d="M3567 1013.3 c-80.8 -4.8 -136.6 -34.8 -164.5 -88.5 -14.2 -27.5 -21.5 -58.4 -23.5 -100.6 -0.5 -11.1 -1 -107.4 -1 -221.4 l0 -201.8 -67.5 0 -67.5 0 0 -53 0 -53 67.5 0 67.5 0 0 -109 0 -109 65 0 65 0 0 109 0 109 94 0 94 0 0 53 0 53 -94 0 -94 0 0.3 211.8 c0.3 211 0.3 211.7 2.4 221.2 4.8 21.6 10.3 33.1 21.3 44.1 8.7 8.7 17.3 13.9 29.8 18 16.7 5.3 24.8 5.9 82.1 5.9 l52.1 0 0 56 0 56 -61.7 -0.2 c-34 -0.1 -64.3 -0.3 -67.3 -0.5z"/>
        <path d="M3980 958 l0 -56 635.5 0 635.5 0 0 56 0 56 -635.5 0 -635.5 0 0 -56z"/>
        <path d="M4259 732.5 l0 -55.5 496 0 496 0 0 55.5 0 55.5 -496 0 -496 0 0 -55.5z"/>
        <path d="M4538 507 l0 -56 356.5 0 356.5 0 0 56 0 56 -356.5 0 -356.5 0 0 -56z"/>
        <path d="M4259 282 l0 -56 496 0 496 0 0 56 0 56 -496 0 -496 0 0 -56z"/>
        <path d="M3015 74.5 l0 -73.5 70.5 0 70.5 0 0 73.5 0 73.5 -70.5 0 -70.5 0 0 -73.5z"/>
        <path d="M1026 73.5 l0 -73.5 70.5 0 70.5 0 0 73.5 0 73.5 -70.5 0 -70.5 0 0 -73.5z"/>
        <path d="M3980 56.5 l0 -55.5 635.5 0 635.5 0 0 55.5 0 55.5 -635.5 0 -635.5 0 0 -55.5z"/>
      </g>
    </svg>
  );
}

export function MiniAidKitLogo({ width, height, fill = "#232323" }: { width: number, height?: number, fill?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" aria-label="Aidkit Symbol" width={width} viewBox="0 0 305.28 243.32">
      <rect fill={fill} width="305.28" height="26.89"/>
      <rect fill={fill} y="216.43" width="305.28" height="26.89"/>
      <rect fill={fill} x="133.89" y="108.21" width="171.39" height="26.89"/>
      <rect fill={fill} x="66.95" y="54.11" width="238.33" height="26.89"/>
      <rect fill={fill} x="66.95" y="162.32" width="238.33" height="26.89"/>
    </svg>
  );
}

export function AddPhoneDashes(phone: string | undefined): string {
  if (phone === undefined || phone === 'undefined') {
    return phone || '';
  }

  const n = phone.replace('+1', '');

  return n.slice(0, 3) + '-' + n.slice(3, 6) + '-' + n.slice(6, n.length);
}

/** localizedStringTemplate should have a [text](url) in there which is where the url will go */
export function useLinksOnLocalizedStrings(localizedStringTemplate: string, replacementLink: string) {
  return useMarkdown(localizedStringTemplate.replace('%url', replacementLink));
}

export async function dataUrlToFile(dataUrl: string, fileName: string): Promise<File> {
  const res: Response = await fetch(dataUrl);
  const blob: Blob = await res.blob();
  return new File([blob], fileName, { type: 'image/png' });
}

export function getDistroBrowserIncompatibility() {
  let supported = true;
  try { new CSSStyleSheet(); } catch (e) { supported = false; }
  if (!supported) {
    // Return a full page element that says this only works in chrome
    return <div className="flex flex-col items-center justify-center h-screen">
      <div className="text-2xl font-bold text-blue-400">This page currently requires Chrome, Firefox, or Safari Version 16.4 or higher.</div>
      <div className="text-xl font-bold text-blue-400">Please use one of these browsers to access this page.</div>
    </div>
  }
  return null;
}

// Storage key for the subsurvey link-key mirror written/read alongside the per-path cookie.
function linkKeyStorageName(pathname: string): string {
  return encodeURIComponent('key:' + pathname);
}

// Best-effort: silently no-ops if sessionStorage is blocked (e.g. some in-app browsers).
export function tryGetLinkKey(pathname: string): string | undefined {
  try { return window.sessionStorage.getItem(linkKeyStorageName(pathname)) ?? undefined; }
  catch (e) { return undefined; }
}

export function trySetLinkKey(pathname: string, key: string): void {
  try { window.sessionStorage.setItem(linkKeyStorageName(pathname), key); }
  catch (e) { /* sessionStorage blocked */ }
}

export function tryRemoveLinkKey(pathname: string): void {
  try { window.sessionStorage.removeItem(linkKeyStorageName(pathname)); }
  catch (e) { /* sessionStorage blocked */ }
}

export function saveKeyToCookieAndRelocateToUrl(url: string, options?: {
  removeHistory?: boolean,
  useHistoryToRelocate?: boolean
}) {
  // Remove this from immediate history
  if (options?.removeHistory) {
    history.replaceState({}, '', '/');
  }

  // Save the key to a cookie and remove it from the URL
  const urlObject = new URL(url);
  const searchParams = urlObject.searchParams;
  if (searchParams.get('key') && !url.includes('card_setup')) {
    const key = searchParams.get('key')!;
    const name = 'key:' + urlObject.pathname;

    // Set the cookie for the root path '/'
    const cookieForRoot = `${encodeURIComponent(name)}=${encodeURIComponent(key)};path=/`;
    document.cookie = cookieForRoot;

    // Set the cookie for the '/p' path
    const cookieForPPath = `${encodeURIComponent(name)}=${encodeURIComponent(key)};path=/p`;
    document.cookie = cookieForPPath;

    // Mirror into sessionStorage to mitigate cases where session cookies are aggressively cleared (e.g. in-app browsers).
    trySetLinkKey(urlObject.pathname, key);

    searchParams.delete('key');
  }
  url = urlObject.toString();

  // Relocate to the URL
  if (options?.useHistoryToRelocate) {
    history.replaceState({}, '', url);
  }
  else {
    (window as any).location = url;
  }
}

/**
 * Returns JSX
 * Useful for dealing with text that may contain RTL characters.
 */
export function wrapWithBdi(text: string | JSX.Element) {
  return <bdi style={{ whiteSpace: 'break-spaces' }}>{text}</bdi>
}

export function renderLinks(text: string) {
  const urlPattern = /(https:\/\/\S+)/g;

  // Split the text into parts based on the URLs
  const parts = text.split(urlPattern);

  // Generate JSX elements for the replaced URLs
  // We have to do this this way because .replace cannot use a JSX Element as the replacer.
  const replacedParts = parts.map((part, index) => {
    if (urlPattern.test(part)) {
      const url = part.trim();
      // If link is long, we need to Shorten it so it doesn't mess up styles
      const shortenedURL = url.length > 50 ? url.substring(0, 50) + '...' : url;
      // here we just render the url but in a <a> just like it would look like in a real text message.
      return <a title={url} key={index} href={url}>{shortenedURL}</a>;
    } else {
      return part;
    }
  });

  // Concatenate the replaced parts into a final JSX element
  return <>{replacedParts}</>
}

export function splitEmailQuote(text: string): { current: string; quoted: string | null } {
  const QUOTE_MARKERS = [
    /^On \w+, \w+ \d+, \d+ at \d+:\d+.*wrote:/m,                             // English Gmail-style
    /^El \w+, \d+ \w+ \d+ a las \d+:\d+.*escribió:/m,                       // Spanish Gmail-style
    /^-{4,}[ \t]*(?:Original Message|Forwarded message)[ \t]*-{4,}/im,       // Outlook-style
    /^_{10,}$/m,                                                               // Outlook underscore separator
  ];

  // Find the earliest match across all markers rather than the first marker that matches
  let earliest: { index: number; matchIndex: number } | null = null;
  for (const marker of QUOTE_MARKERS) {
    const match = marker.exec(text);
    if (match?.index !== undefined && (earliest === null || match.index < earliest.index)) {
      earliest = { index: match.index, matchIndex: match.index };
    }
  }

  if (earliest !== null) {
    const beforeMatch = text.slice(0, earliest.index);
    const lastNewline = beforeMatch.lastIndexOf('\n');
    const splitPoint = lastNewline === -1 ? earliest.index : lastNewline;
    const current = text.slice(0, splitPoint).trim();
    if (current.length > 0) {
      return { current, quoted: text.slice(splitPoint).trim() };
    }
  }
  return { current: text, quoted: null };
}

export function stripSubjectPrefix(rawMessage: string, subject: string | undefined, direction: string): string {
  if (subject && direction === 'inbound' && rawMessage.startsWith(subject + ': ')) {
    return rawMessage.slice(subject.length + 2);
  }
  return rawMessage;
}

export function getEmailHtmlContent(renderedMessage: string | undefined, messageBody: string): string | null {
  if (renderedMessage) return renderedMessage;
  if (messageBody.trimStart().startsWith('<')) return messageBody;
  return null;
}

export function blockify(text: string, callback?: (str: string) => JSX.Element) {
  return text.split('\n').map((str, index) => <p key={str + "-" + index} className="mb-0">{str === '' ? <br /> : (callback?.(str) || str)}</p>);
}

export function useOnlineDetection(config?: PublicConfig): ContextType<typeof OnlineAwarenessContext> {
  const [online, setOnline] = useState(true);
  const [inBackground, setInBackground] = useState(false);
  const [status, setStatus] = useState<OfflineStatus>('unloaded');

  useEffect(() => {
    const nowInBackground = () => setInBackground(true);
    const nowInForeground = () => setInBackground(false);
    if (config?.experimental?.enableOfflineMode) {
      window.addEventListener('blur', nowInBackground);
      window.addEventListener('focusout', nowInBackground);
      window.addEventListener('focus', nowInForeground);
      window.addEventListener('focusin', nowInForeground);
    }

    return () => {
      window.removeEventListener('blur', nowInBackground);
      window.removeEventListener('focusout', nowInBackground);
      window.removeEventListener('focus', nowInForeground);
      window.removeEventListener('focusin', nowInForeground);
    }
  }, [config]);

  useEffect(() => {
    if (inBackground) return;

    if (config?.experimental?.enableOfflineMode) {
      // gating this to 30 seconds at a minimum to mitigate accidental abuse of our servers
      const intervalInMS = 30000;
      offlineLogger.debug(`setting up online detection for every ${intervalInMS}ms`);
      const token = setInterval(() =>
        fetch("/favicon.ico", {
          method: 'HEAD',
          keepalive: false,
          cache: 'no-store'
        })
          .then(response => setOnline(response.ok))
          .catch(() => setOnline(false)),
      intervalInMS
      );
      // do an initial fetch on focus to ensure
      // we have the latest state
      fetch("/favicon.ico", {
        method: 'HEAD',
        keepalive: false,
        cache: 'no-store'
      })
        .then(response => setOnline(response.ok))
        .catch(() => setOnline(false))

      const handleOnline = () => setOnline(true);
      const handleOffline = () => setOnline(false);

      // these window events are supported on Chrome, and provide a real-time way
      // to determine the browser's network connectivity.
      // Any server outages will be caught by the preceeding /favicon.ico check.
      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);
      return () => {
        offlineLogger.debug('disabling online detection');
        clearInterval(token);
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
      }
    } else {
      offlineLogger.debug('online detection not enabled');
    }
  }, [inBackground, config]);

  useEffect(() => {
    if (!navigator.serviceWorker || !(config?.experimental?.enableOfflineMode)) {
      return;
    }

    function stateChange(event: any) {
      if (!event.data) {
        return;
      }

      const { data: { type, state, timeTaken, failed } }: { data: { failed: any, type: string, state: string, timeTaken?: number } } = event;

      if (type === 'offline-warm') {
        setStatus(state as OfflineStatus);
        if (failed && failed.length > 0) {
          const msgIds = [];
          for (const failure of failed) {
            const error = new Error(`${failure.path}: ${failure.error?.message ?? failure.error}`)
            msgIds.push(
              captureException(error, { level: 'error', tags: { phase: 'warmup' } })
            );
          }
          offlineLogger.warn('failures during warmup', failed, msgIds);
        }
      } else if (type === 'offline-sync') {
        setStatus(state === 'complete' ? 'synced' : 'syncing');
        if (timeTaken) {
          offlineLogger.debug(`Offline sync took ${timeTaken}ms`);
        }
      }
    };

    navigator.serviceWorker.addEventListener('message', stateChange);
    return () => {
      navigator.serviceWorker.removeEventListener('message', stateChange);
    }
  }, [config?.experimental?.enableOfflineMode]);


  return { online, status };
}

export function useOfflineSync(config?: PublicConfig): ContextType<typeof OfflineSyncContext> {

  const applicantDir = useRef<PermaCache<string, RequestEntry>>();
  const [statuses, setStatuses] = useState<Record<string, SyncState>>({});

  useEffect(() => {
    if (!navigator.serviceWorker || !(config?.experimental?.enableOfflineMode)) {
      return;
    }

    (async () => {

      if (!applicantDir.current) {
        applicantDir.current = await getOrCreateHttpCache('applicants');
      }

      console.debug('useOfflineSync: ', 'getting applicant base states');

      const applicants = await Promise.all((await applicantDir.current.dirs()).map(async d => {
        try {
          const app = await d.as(toApplicant);
          return await app.state();
        } catch {
          return { id: d.name, status: SyncStatus.Unavailable };
        }
      }));

      setStatuses(applicants.reduce((prev, cur) => ({ ...prev, [cur.id!]: cur }), {}));
    })()
  }, [config?.experimental?.enableOfflineMode]);

  useEffect(() => {
    if (!navigator.serviceWorker || !(config?.experimental?.enableOfflineMode)) {
      return;
    }

    function stateChange(event: MessageEvent<SyncStateEvent>) {
      if (!event.data) {
        return;
      }

      const { data } = event;

      if (data.type === 'offline-sync') {
        if (data.id) {
          setStatuses(prev => (
            { ...prev, [data.id!]: data }
          ));
        }
      }
    };

    navigator.serviceWorker.addEventListener('message', stateChange);
    return () => {
      navigator.serviceWorker.removeEventListener('message', stateChange);
    }
  }, [config?.experimental?.enableOfflineMode]);

  return { statuses };
}

export function BarSpinner() {
  return <div className="spinner">
    <div className="rect1"></div>&nbsp;
    <div className="rect2"></div>&nbsp;
    <div className="rect3"></div>&nbsp;
    <div className="rect4"></div>&nbsp;
    <div className="rect5"></div>
  </div>
}

/**
 * A logger that only logs error messages in production,
 * and everything else in dev.
 *
 * @example
 * export function MyButton() {
 *  const logger = useLogger(MyButton);
 *  logger.info('this is a button saying stuff');
 *  return (<button>Wicked Cool™</button>);
 * }
 *
 * outputs to the console:
 * >> (Info-Colored MyButton Badge) this is a button saying stuff
 */
export function useLogger(fn: Function | string, props?: any) {
  return createLogger(
    `${typeof fn === 'function' ? fn.name : fn}${props ? JSON.stringify(props) : ''}`,
    DefaultStyles(DefaultColors), ['debug', 'assert', 'warn', 'info']
  );
}

export function getLocalTimezoneSuffix() {
  const dstStart = new Date(Date.UTC(new Date().getFullYear(), 2, 1)).getUTCDay() === 0 ? 8 : 15; // 2nd Sunday of March
  const dstEnd = new Date(Date.UTC(new Date().getFullYear(), 10, 1)).getUTCDay() === 0 ? 1 : 8; // 1st Sunday of November

  const month = new Date().getMonth();
  const isDST = month >= dstStart && month <= dstEnd;  // Assuming months start from 1 for Jan and end at 12 for Dec

  const offset = new Date().getTimezoneOffset() / 60;

  switch (offset) {
    case 4:
      return isDST ? 'EDT' : 'EDT';
    case 5:
      return isDST ? 'EST' : 'CDT';
    case 6:
      return isDST ? 'CST' : 'MDT';
    case 7:
      return isDST ? 'MST' : 'PDT';
    case 8:
      return isDST ? 'PDT' : 'PDT';
    default:
      return '';
  }
}

export const prettyPhone: (phone: string) => string = (phone: string) => {
  if (phone.length === 10) {
    return phone.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
  }
  if (phone.startsWith('+1') && phone.length > 10) {
    let phoneNumber = phone.slice(phone.length - 10);
    return phone.slice(0, phone.length - 10) + ' ' + prettyPhone(phoneNumber);
  }
  return phone;
}

/**
 * Sorts records a and b using sortKey in order specified by sortOrder.
 * Importantly, this checks if the values being sorted are numbers, and if so sorts them as such,
 * thus avoiding the issue of numbers as strings being sorted lexographically ("1", "22", "5")
 */
function sortFunc(a: Record<string, any>, b: Record<string, any>, sortKey: string, sortOrder: 'ascending' | 'descending') {
  const aValue = a[sortKey];
  const bValue = b[sortKey];

  const aValueIsNumber = !isNaN(parseFloat(aValue)) && isFinite(aValue);
  const bValueIsNumber = !isNaN(parseFloat(bValue)) && isFinite(bValue);

  if (aValueIsNumber && bValueIsNumber) {
    return sortOrder === 'ascending'
      ? parseFloat(aValue) - parseFloat(bValue)
      : parseFloat(bValue) - parseFloat(aValue);
  }

  if (aValue === null || aValue === undefined) return sortOrder === 'ascending' ? -1 : 1;
  if (bValue === null || bValue === undefined) return sortOrder === 'ascending' ? 1 : -1;

  let normalA = (aValue || '').toString().toLowerCase().trim();
  let normalB = (bValue || '').toString().toLowerCase().trim();

  if (normalA < normalB) {
    return sortOrder === 'ascending' ? -1 : 1;
  }
  if (normalA > normalB) {
    return sortOrder === 'ascending' ? 1 : -1;
  }
  return 0;
}

/**
 * Sorts the given records using the provided key in the array of records.
 */
export function sortRecordsInPlace(records: Record<string, any>[], sortKey: string, sortOrder: 'ascending' | 'descending') {
  records.sort((a, b) => sortFunc(a, b, sortKey, sortOrder));
}

/**
 * Creates a copy of the given records and sorts it using the provided key in the array of records.
 */
export function copyAndSortRecords(records: Record<string, any>[], sortKey: string, sortOrder: 'ascending' | 'descending') {
  return [...records].sort((a, b) => sortFunc(a, b, sortKey, sortOrder));
}

/**
 * Creates a copy of the given records and returns all records r such that for key k,v in matchingRecord, v is included in r[k].
 */
export function filterRecords(records: Record<string, any>[], matchingRecord: Record<string, string>) {
  return records.filter(r => {
    return Object.keys(matchingRecord).every(
      (matchKey) => {
        let recordMatchKey = matchKey;
        if (matchKey === 'full_uid') {
          recordMatchKey = 'uid';
        }
        return r[recordMatchKey] && JSON.stringify(r[recordMatchKey]).toLowerCase().includes((matchingRecord[matchKey]).toLowerCase())
      });
  });
}

export function isPhone() {
  const userAgent = navigator.userAgent || navigator.vendor;
  return /iphone|android/i.test(userAgent.toLowerCase());
}

export function classNames(...classes: (string | undefined | null | boolean)[]) {
  return classes.filter(Boolean).join(' ')
}

export function generateRandomString(length: number, chars: string): string {
  let result = '';
  const charsLength = chars.length;
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * charsLength));
  }
  return result;
}

export function formatTime(totalSeconds: number) {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
}

export function removeDuplicateEvents(events: ChangeSet) {
  let eventIDs = new Set<string>();
  let uniqueEvents: ChangeSet = [];
  events?.forEach(e => {
    if (!eventIDs.has(e[2].id)) {
      eventIDs.add(e[2].id);
      uniqueEvents.push(e);
    }
  })

  return uniqueEvents;
}

type ComponentType =
  v0.Block
  | v0.Collection
  | v0.TopLevelTemplatedBlock
  | v0.Payment
  | v0.NotificationGroup
  | v0.Dashboard
  | v0.UsioMailing
  | v0.GiveCardMailing
  | v0.USBankCardMailing
  | v0.QualtricsResponseHandler
  | v0.ApplicantIdentities
  | v0.Macro
  | v0.ApplicantPortalPage
  | v0.AIAssistantReviewSurvey;

export function memoize<T = any, P extends any[] = any[]>(fn: (...args: P) => T, duration?: number): (...args: P) => T {
  const cache: Record<string, T> = {};
  const timeouts: Record<string, NodeJS.Timeout | undefined> = {};
  return function _memoizedInner(...args: P) {
    const key = JSON.stringify(args);
    if (!cache[key]) {
      cache[key] = fn(...args);
      if (duration) {
        if (timeouts[key]) {
          timeouts[key] = clearTimeout(timeouts[key]) as undefined;
        }
        timeouts[key] = setTimeout(
          () => {
            delete cache[key];
            if (timeouts[key]) {
              timeouts[key] = clearTimeout(timeouts[key]) as undefined;
            }
          },
          duration
        );
      }
    }
    return cache[key];
  }
}

export function lowerCaseCompare(item: string, comparisons: string[]) {
  return comparisons.some(c => c.toLowerCase().includes(item.toLowerCase()));
}

/**
 * Utility function to iterate over cookies and decide which to remove.
 * NOTE: we remove all cookies using / as path. We can update this to be more specific if needed.
 * @param {(name: string) => boolean} shouldRemove - A callback to determine if a cookie should be removed.
 */
function handleCookies(shouldRemove: (name: string) => boolean) {
  document.cookie.split(';').forEach(cookie => {
    const eqPos = cookie.indexOf('=');
    const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();

    if (shouldRemove(name)) {
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
    }
  });
}

/**
 * Remove specific cookies by name.
 * @param {string[]} toRemove - List of cookie names to remove.
 */
export function removeSpecificCookies(toRemove: string[]) {
  handleCookies(name => toRemove.includes(name));
  // The portal token has a sessionStorage mirror (see PortalSession) that getPortalToken
  // falls back to; tear it down with the cookie or the applicant keeps looking logged in.
  if (toRemove.includes('portal')) tryRemovePortalToken();
}

/**
 * Remove all cookies except those specified.
 * @param {string[]} toKeep - List of cookie names to keep.
 */
export function removeAllButSpecificCookies(toKeep: string[]) {
  handleCookies(name => !toKeep.includes(name));
}

/**
 * Remove all cookies.
 */
export function removeAllCookies() {
  handleCookies(() => true);
}

export const DISTRO_DEFAULT_LIGHTNESS = '70%';

export function stringToColor(str: string, lightness?: string) {
  if (!str) return 'hsl(0%,0%,0%)';

  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return `hsl(${(hash % 360)}, 60%, ${lightness || '90%'})`;
}

export function categorizeSignedURLs(signedURLs: string[] = []): { imageFileUrls: string[], videoFileUrls: string[], audioFileUrls: string[] } {
  const imageFileUrls: string[] = [];
  const videoFileUrls: string[] = [];
  const audioFileUrls: string[] = [];
  signedURLs.forEach((signedURL: string) => {
    const url = new URL(signedURL);
    if (!!(url.pathname?.match(/\.mp4$|\.ogg$|\.webm$/)?.[0])) {
      videoFileUrls.push(signedURL);
    } else if (!!(url.pathname?.match(/\.mp3$/)?.[0])) {
      audioFileUrls.push(signedURL);
    } else {
      imageFileUrls.push(signedURL);
    }
  });
  return { imageFileUrls, videoFileUrls, audioFileUrls };
}

/**
 * A list of patterns that are considered truly critical errors that could cause crashes or severe issues.
 * These represent fundamental React violations or memory/performance problems that make the app unusable.
 */
const FATAL_PATTERNS: RegExp[] = [
  // React Hook violations - these violate React's fundamental rules and can cause crashes
  /React Hook .* is called conditionally/,
  /React Hook .* cannot be called inside a callback/,
  /React Hook .* is called in function .* that is neither a React function component nor a custom React Hook/,
  /Invalid hook call/,
  
  // Memory leak warnings - these can make the app unusable over time
  /Warning: Can't perform a React state update on an unmounted component/,
  /Warning: Can't perform a React state update on a component that isn't mounted/,
  
  // State errors that can cause infinite loops or crashes
  /Warning: Maximum update depth exceeded/,
  /Warning: Cannot update during an existing state transition/,
  /Warning: setState.*cannot update a component while rendering a different component/,
]

/**
 * If in development mode, wraps console.error with a function that will detect certain error logs
 * and actually throw an error. This makes it significantly easier to catch sneaky React errors in local development.
 */
export function installFatalErrorTrapInDev() {
  if (process.env.NODE_ENV === 'development') {
    const originalError = console.error;

    console.error = function (...args) {
      const message = args?.[0]?.toString?.() ?? '';

      if (FATAL_PATTERNS.some((pattern) => pattern.test(message))) {
      // Escalate to hard crash
        throw new Error(`[POTENTIALLY FATAL ERROR DETECTED]: ${message}`);
      }

      originalError.apply(console, args);
    };
  }
}

/**
 * Useful for when the URL that would be clicked is cross-domain,
 * which means that the normal approach of `<a href="https://blah.com" download="filename.png" />`
 * won't initiate a download, but instead will navigate to a new tab.
 *
 * This causes the browser to download the file, with the given file name.
 *
 * WARNING: this means that we download the file BEFORE it gets "downloaded" in the browser...
 * So typical download progress won't really reflect actual timing. So only use this 
 * if there isn't a way to initiate the download via the <a> tag normally.
 */
export function forceInitiateFileDownload(blob: Blob, name: string) {
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = name;

  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
  URL.revokeObjectURL(blobUrl);
}

/**
 * Detects if the user is browsing from an in-app browser and returns the platform name
 * Returns the platform name if detected, undefined otherwise
 * Currently detects: Instagram, Facebook
 * Easily extendable to add more platforms - just add new conditions to the list
 */
export function detectInAppBrowser(): string | undefined {
  const userAgent = navigator.userAgent || '';

  // Order matters - check more specific patterns first
  if (userAgent.includes('Instagram')) {
    return 'Instagram';
  }
  if (userAgent.includes('FBAN') || userAgent.includes('FB_IAB') || userAgent.includes('Facebook')) {
    return 'Facebook';
  }

  // Add more platforms here as needed
  // if (userAgent.includes('Twitter')) return 'Twitter';
  // if (userAgent.includes('LinkedIn')) return 'LinkedIn';

  return undefined;
}

export function GoBackToSectionButton({ sectionId }: { 
  sectionId: string
}) {
  const L = useLocalizedStrings();
  const { navigateToSection } = useSurveySections();

  return <Button
    variant="primary"
    icon={<Icon name="ArrowLeftCircleIcon" />}
    onClick={() => {
      navigateToSection(sectionId)
    }}>
    {L.ai_coach.document_signals.go_back_and_make_changes}
  </Button>  
}

export function shouldIncludeQuestionInAppState(q: Question | QuestionProps) : boolean { 
  return (!q["Additional Options"]?.includes('Hidden') || q["Field Type"] === "Computed") && !['Show Field', 'Explanation', 'Action'].includes(q["Field Type"])
}


/**
 * Safe check for a usable opener. COOP (Cross-Origin-Opener-Policy) can block
 * access to window.opener / window.opener.closed; treat that as no opener.
 */
export function hasUsableOpener(): boolean {
  try {
    return !!(window.opener && !window.opener.closed);
  } catch {
    return false;
  }
}

const LIVENESS_RETURN_URL_KEY = 'liveness_return_url';

export function rememberLivenessReturnUrl(url: string) {
  try {
    window.sessionStorage.setItem(LIVENESS_RETURN_URL_KEY, url);
  } catch {
    // storage blocked
  }
}

export function livenessReturnUrl(surveyUrl?: string): string | undefined {
  if (surveyUrl) return surveyUrl;
  try {
    return window.sessionStorage.getItem(LIVENESS_RETURN_URL_KEY) || undefined;
  } catch {
    return undefined;
  }
}

export function canReturnToApplication(surveyUrl?: string) {
  return hasUsableOpener() || !!livenessReturnUrl(surveyUrl);
}

export function returnToApplication(surveyUrl?: string) {
  const url = livenessReturnUrl(surveyUrl);
  if (!hasUsableOpener() && url) {
    window.location.href = url;
  } else {
    window.close();
  }
}

export function fileNameFromUrl(url: string): string {
  // Take the last non-empty path segment so trailing-slash URLs
  // (".../testpdf.pdf/") still resolve to the filename rather than the
  // whole URL.
  const segments = url.split("/").filter(Boolean);
  const lastSegment = segments.pop() || url;
  try {
    return decodeURIComponent(lastSegment);
  } catch {
    return lastSegment;
  }
}
